import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import type { RumEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import ReactGA from 'react-ga4';
import useTTI from '../hooks/useTTI';
import { useSession } from '~/hooks/useSession';

// Drop expected auth-flow 4xx failures so they don't inflate the RUM error rate.
// A wrong password, mistyped email, or expired session is a business outcome, not an application error.
const beforeSend = (event: RumEvent): boolean => {
  if (event.type !== 'error') return true;
  const message = event.error?.message ?? '';
  const viewPath = event.view?.url ?? '';

  // 400/401/403 on the login page are user-input or credential failures, surfaced to users via notifications.
  if (/\/login(\?|$|\/)/.test(viewPath) && /status code 40[013]\b/.test(message)) return false;

  // Email format validation errors (user-input, not application errors) on any page.
  if (/should match format "email"/.test(message)) return false;

  // Browser resize-observer safety valve, emitted by antd v4's rc-resize-observer (Table measurement,
  // Typography ellipsis). Observations that don't fit in one frame are re-delivered the next frame;
  // spec authors call the error benign: https://github.com/WICG/resize-observer/issues/38
  // Matches both wordings: "loop completed with undelivered notifications" and "loop limit exceeded".
  if (message.includes('ResizeObserver loop')) return false;

  return true;
};

const DatadogProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { user } = useSession();

  useEffect(() => {
    // should only once on mount
    // initialize Datadog RUM and Google analytics if we are running in stage or production
    if (process.env.NODE_ENV === 'production') {
      if (/stage/.test(window.location.hostname)) {
        ReactGA.initialize('G-5Y9C6SJ5SR');
        datadogRum.init({
          clientToken: 'pub7c49e64338f28bbcfdd214024edc39cb',
          applicationId: '78024c2c-dfb5-43a2-a1ee-c92221b0ea73',
          service: 'partner-console',
          env: 'stage',
          trackUserInteractions: true, //frustration signals, click maps, scroll maps
          version: process.env.REACT_APP_VERSION,
          allowedTracingUrls: [new RegExp(`^${process.env.REACT_APP_GATEWAY_URI}/.*`)],
          beforeSend,
        });
      } else {
        ReactGA.initialize('G-3Q56ZJDVFX');
        datadogRum.init({
          clientToken: 'pubc905b400161f216ec2d4e3b19f0d2a3c',
          applicationId: 'a95647e6-fb4e-4b97-8569-26a146597b80',
          service: 'partner-console',
          env: 'production',
          trackUserInteractions: true, //frustration signals, click maps, scroll maps
          version: process.env.REACT_APP_VERSION,
          allowedTracingUrls: [new RegExp(`^${process.env.REACT_APP_GATEWAY_URI}/.*`)],
          beforeSend,
        });
      }
    }

    //used for local dev testing in stage env
    if (process.env.NODE_ENV === 'development') {
      datadogRum.init({
        clientToken: 'pub7c49e64338f28bbcfdd214024edc39cb',
        applicationId: '78024c2c-dfb5-43a2-a1ee-c92221b0ea73',
        service: 'partner-console',
        env: 'local-test',
        trackUserInteractions: true, //frustration signals, click maps, scroll maps
        version: process.env.REACT_APP_VERSION,
        allowedTracingUrls: [new RegExp(`^${process.env.REACT_APP_GATEWAY_URI}/.*`)],
        beforeSend,
      });
    }
  }, []);

  useEffect(() => {
    if (user?.sessionPartnerId && user?.sessionPartnerName) {
      datadogRum.setGlobalContext({
        partnerId: user.sessionPartnerId,
        partnerName: user.sessionPartnerName,
        userId: user.userId,
        userEmail: user.email,
      });
    }
  }, [user?.sessionPartnerId, user?.sessionPartnerName, user?.userId, user?.email]);

  useTTI();

  return <>{children}</>;
};

export default DatadogProvider;
